import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";

interface CopyrightNotification {
  id: string;
  created_at: string;
  message: string;
  read: boolean;
}

const CopyrightNotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<CopyrightNotification[]>([]);
  const [open, setOpen] = useState(false);

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      const { data } = await supabase
        .from("copyright_notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20) as { data: CopyrightNotification[] | null };

      if (data) setNotifications(data);
    };

    load();
  }, [user]);

  const markAllRead = async () => {
    if (!user || unreadCount === 0) return;

    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    await supabase
      .from("copyright_notifications")
      .update({ read: true } as any)
      .in("id", unreadIds);

    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  if (!user || notifications.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markAllRead(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-display font-semibold text-sm">Copyright Notices</h3>
        </div>
        <ScrollArea className="max-h-[420px]">
          {notifications.map((n) => (
            <div
              key={n.id}
              className={`px-4 py-3 border-b border-border/50 text-sm ${!n.read ? "bg-primary/5" : ""}`}
            >
              <p className="text-muted-foreground text-xs leading-relaxed whitespace-pre-line">{n.message}</p>
              <p className="text-muted-foreground/50 text-[11px] mt-2">
                {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
              </p>
            </div>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default CopyrightNotificationBell;
